import { useEffect, useState } from "react";
import { Pencil, Plus, X } from "lucide-react";
import { Field, TextInput, TextArea, SelectInput } from "@/components/brand/Field";
import { api, formatApiError } from "@/lib/api";

const slugify = (t) => t.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/[\s_]+/g, "-").replace(/-+/g, "-").slice(0, 80);

const EMPTY = { title: "", slug: "", kind: "concept", label: "Concept Project", summary: "", tags: "", image: "", body: "", order: 0, status: "published" };

const AdminPortfolio = () => {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = () => api.get("/admin/portfolio").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openEdit = (p) => {
    setEdit(p);
    setF(p.id ? { ...p, tags: (p.tags || []).join(", "), body: p.body || "" } : EMPTY);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    const payload = {
      ...f,
      tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
      order: Number(f.order) || 0,
      image: f.image || null,
      body: f.body || null,
    };
    try {
      if (edit.id) await api.put(`/admin/portfolio/${edit.id}`, payload);
      else await api.post("/admin/portfolio", payload);
      setEdit(null);
      load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    setSaving(false);
  };

  const unpublish = async (p) => { await api.delete(`/admin/portfolio/${p.id}`); load(); };

  return (
    <div className="space-y-6" data-testid="admin-portfolio">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="space-y-1">
          <h1 className="font-display text-2xl font-bold text-brand-snow sm:text-3xl">Portfolio & case studies</h1>
          <p className="text-sm text-brand-mist">{items.length} items. Concepts stay clearly labelled; case studies publish only with client consent.</p>
        </div>
        <button onClick={() => openEdit({})} data-testid="portfolio-new" className="inline-flex items-center gap-2 rounded-full bg-brand-emerald px-5 py-2.5 font-display text-sm font-semibold text-brand-black hover:bg-brand-snow">
          <Plus size={15} /> Add project
        </button>
      </div>
      <div className="surface-card divide-y divide-brand-mist/10" data-testid="portfolio-table">
        {items.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-4 px-6 py-4" data-testid={`portfolio-row-${p.slug}`}>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-brand-snow">{p.title}</p>
              <p className="text-xs text-brand-mist">{p.kind} · {p.label} · {p.status}</p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button onClick={() => openEdit(p)} data-testid={`portfolio-edit-${p.slug}`} className="rounded-full border border-brand-mist/25 px-3 py-1.5 text-xs text-brand-snow hover:border-brand-emerald hover:text-brand-emerald"><Pencil size={12} /></button>
              {p.status === "published" && (
                <button onClick={() => unpublish(p)} data-testid={`portfolio-unpublish-${p.slug}`} className="rounded-full border border-brand-mist/25 px-3 py-1.5 text-xs text-brand-mist hover:border-red-400 hover:text-red-400">Unpublish</button>
              )}
            </div>
          </div>
        ))}
      </div>

      {edit && (
        <div className="fixed inset-0 z-50 flex justify-center bg-brand-black/70 backdrop-blur-sm sm:p-6" onClick={() => setEdit(null)} data-testid="portfolio-editor-modal">
          <form onSubmit={save} className="h-full w-full max-w-2xl space-y-5 overflow-y-auto border border-brand-mist/15 bg-brand-charcoal p-6 sm:h-auto sm:max-h-[90vh] sm:rounded-2xl sm:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-brand-snow">{edit.id ? `Edit: ${edit.title}` : "New project"}</h2>
              <button type="button" onClick={() => setEdit(null)} aria-label="Close" className="rounded-md p-2 text-brand-mist hover:text-brand-snow"><X size={18} /></button>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Title" htmlFor="po-title" required><TextInput id="po-title" testid="po-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value, slug: edit.id ? f.slug : slugify(e.target.value) })} required /></Field>
              <Field label="Slug" htmlFor="po-slug"><TextInput id="po-slug" testid="po-slug" value={f.slug} onChange={(e) => setF({ ...f, slug: slugify(e.target.value) })} required /></Field>
              <Field label="Type" htmlFor="po-kind">
                <SelectInput id="po-kind" testid="po-kind" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value, label: e.target.value === "case-study" ? "Case Study" : "Concept Project" })}>
                  <option value="concept">Concept project / demonstration</option>
                  <option value="case-study">Case study (real client work)</option>
                </SelectInput>
              </Field>
              <Field label="Public label" htmlFor="po-label"><TextInput id="po-label" testid="po-label" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} /></Field>
              <Field label="Order" htmlFor="po-order"><TextInput id="po-order" type="number" testid="po-order" value={f.order} onChange={(e) => setF({ ...f, order: e.target.value })} /></Field>
              <Field label="Status" htmlFor="po-status">
                <SelectInput id="po-status" testid="po-status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </SelectInput>
              </Field>
            </div>
            <Field label="Summary" htmlFor="po-summary" required><TextArea id="po-summary" rows={3} testid="po-summary" value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} required /></Field>
            <Field label="Tags (comma separated)" htmlFor="po-tags"><TextInput id="po-tags" testid="po-tags" value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} /></Field>
            <Field label="Image URL" htmlFor="po-image"><TextInput id="po-image" testid="po-image" value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} placeholder="https://…" /></Field>
            <Field label="Detail body" htmlFor="po-body" hint="Optional. '## ' headings, '- ' bullets, blank line between paragraphs.">
              <TextArea id="po-body" rows={8} testid="po-body" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} />
            </Field>
            {error && <p role="alert" className="text-sm text-red-400" data-testid="portfolio-editor-error">{error}</p>}
            <button type="submit" disabled={saving} data-testid="portfolio-save" className="rounded-full bg-brand-emerald px-6 py-3 font-display text-sm font-semibold text-brand-black hover:bg-brand-snow disabled:opacity-60">
              {saving ? "Saving…" : edit.id ? "Save changes" : "Create project"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminPortfolio;
